import { useEffect, useState, useRef } from "react";
import Lenis from "lenis";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ShoppingCart, ArrowDown, ArrowUpRight, Truck, ShieldCheck, Star, Plus,
  PackageCheck, PlugZap, MessagesSquare, MessageCircle, Phone, Mail, MapPin, Check,
} from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import CartDrawer from "../components/site/CartDrawer";

const aud = (n) => `$${Number(n).toFixed(0)}`;
const HERO_LINES = ["EVERY", "CHANNEL.", "ONE BOX."];

const NAV = [
  { href: "#shop", label: "Boxes" },
  { href: "#plans", label: "Subscriptions" },
  { href: "#why", label: "Why us" },
  { href: "#guides", label: "Guides" },
  { href: "#faq", label: "FAQ" },
];

function Header() {
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${scrolled ? "bg-[#F8F9FA]/80 backdrop-blur-xl border-b border-slate-200" : ""}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2.5 font-display font-black text-lg tracking-tight uppercase" data-testid="logo"><img src="/logo.png?v=2" alt="Indian TV Box logo" className="w-9 h-9 rounded-lg" /><span>INDIANTV<span className="text-[#FF5722]">.</span>BOX</span></a>
        <nav className="hidden md:flex items-center gap-8 text-sm font-bold text-slate-600">
          {NAV.map((n) => <a key={n.href} href={n.href} className="hover:text-[#FF5722] transition-colors">{n.label}</a>)}
        </nav>
        <div className="flex items-center gap-3">
          <button onClick={() => setOpen(true)} data-testid="open-cart-btn" className="relative p-2">
            <ShoppingCart size={20} />
            {count > 0 && <span className="absolute -top-0.5 -right-0.5 bg-[#FF5722] text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{count}</span>}
          </button>
          <a href="#shop" className="hidden sm:inline-flex bg-[#FF5722] text-white rounded-full px-6 py-2.5 font-display font-bold text-sm uppercase tracking-wide hover:bg-[#0F172A] transition-colors">Shop now</a>
        </div>
      </div>
    </header>
  );
}

function Hero({ featured }) {
  return (
    <section id="top" className="relative min-h-screen flex items-center overflow-hidden bg-[#F8F9FA] grain pt-16">
      <div className="hero-blob w-[34rem] h-[34rem] bg-[#FF5722]/25 -top-32 -left-32" />
      <div className="hero-blob w-[30rem] h-[30rem] bg-[#4F46E5]/20 top-1/3 right-[-8rem]" />
      <div className="hero-blob w-[24rem] h-[24rem] bg-[#FFB300]/20 bottom-[-6rem] left-1/3" />
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-16 items-center w-full">
        <div className="lg:col-span-7">
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="text-xs font-black uppercase tracking-[0.25em] text-[#4F46E5] mb-8 flex items-center gap-3">
            <span className="w-8 h-px bg-[#4F46E5]" />Australia's IPTV Box Store
          </motion.p>
          <h1 className="font-display font-black uppercase leading-[0.9] tracking-tighter text-6xl sm:text-7xl lg:text-8xl xl:text-9xl text-[#0F172A]">
            {HERO_LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span className="block" initial={{ y: "115%" }} animate={{ y: "0%" }}
                  transition={{ duration: 0.8, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}>
                  {i === 1 ? <span className="text-[#FF5722]">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
            className="mt-8 max-w-md text-base sm:text-lg font-medium text-slate-600 leading-relaxed">
            Premium 4K/8K IPTV set-top boxes, shipped from Melbourne. Plug in tonight, stream everything tomorrow.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4">
            <a href="#shop" data-testid="hero-shop-btn" className="group bg-[#FF5722] text-white rounded-full px-8 py-4 font-display font-bold text-base uppercase tracking-wide hover:bg-[#0F172A] transition-colors duration-300 flex items-center gap-2">
              Shop the boxes <ArrowDown size={16} className="group-hover:translate-y-1 transition-transform duration-300" />
            </a>
            <a href="#guides" className="rounded-full px-8 py-4 font-display font-bold text-base uppercase tracking-wide border-2 border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white transition-colors duration-300">Setup guides</a>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
            className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-sm font-bold text-slate-500">
            <span className="flex items-center gap-2"><Truck size={16} className="text-[#4F46E5]" /> Ships from Melbourne</span>
            <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-[#4F46E5]" /> 12-month warranty</span>
            <span className="flex items-center gap-2"><Star size={16} className="text-[#FF5722] fill-[#FF5722]" /> 4.9 from 2,300+ reviews</span>
          </motion.div>
        </div>
        <div className="lg:col-span-5 relative" style={{ perspective: 1200 }}>
          <motion.div data-testid="hero-product-card" initial={{ opacity: 0, y: 60, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ delay: 0.4, duration: 0.8 }}
            className="relative bg-white border border-slate-200 rounded-3xl p-6 shadow-[0_40px_80px_-30px_rgba(15,23,42,0.25)]">
            <div className="overflow-hidden rounded-2xl bg-slate-50">
              <img alt={featured?.name || "IPTV box"} src={featured?.image} className="w-full h-80 sm:h-96 object-cover" />
            </div>
            <div className="mt-5 flex items-end justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-[#FF5722]">Best seller</p>
                <p className="font-display font-bold text-xl text-[#0F172A] mt-1">{featured?.name || "—"}</p>
              </div>
              <p className="font-display font-black text-2xl text-[#0F172A]">{featured ? aud(featured.price) : ""}</p>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1 }}
            className="absolute -bottom-6 -left-6 bg-[#0F172A] text-white rounded-2xl px-5 py-4 shadow-xl">
            <p className="text-xs font-black uppercase tracking-widest text-slate-400">Tonight's viewing</p>
            <p className="font-display font-bold text-lg">Sorted in 5 minutes flat</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

const MARQUEE = ["4K ULTRA HD", "AU STOCK — SHIPS FROM MELBOURNE", "PAYPAL & BANK TRANSFER", "12-MONTH WARRANTY", "PLUG & PLAY IN 5 MINUTES", "REAL AUSSIE SUPPORT"];
function TickerStrip() {
  return (
    <div className="bg-white border-y border-slate-200 py-5 overflow-hidden">
      <Marquee speed={40} gradient={false}>
        {MARQUEE.map((t, i) => (
          <span key={i} className="flex items-center shrink-0">
            <span className={`font-display font-black uppercase tracking-[0.2em] text-lg sm:text-xl px-6 ${i % 2 ? "text-outline" : "text-[#0F172A]"}`}>{t}</span>
            <span className="w-2 h-2 rounded-full bg-[#FF5722] shrink-0" />
          </span>
        ))}
      </Marquee>
    </div>
  );
}

const Overline = ({ children, color = "#4F46E5" }) => (
  <p className="text-xs font-black uppercase tracking-[0.25em] flex items-center gap-3" style={{ color }}>
    <span className="w-8 h-px" style={{ background: color }} /> {children}
  </p>
);

function ProductCard({ p }) {
  const { add } = useCart();
  return (
    <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
      className="group bg-white border border-slate-200 rounded-3xl overflow-hidden flex flex-col" data-testid={`product-${p.id}`}>
      <Link to={`/product/${p.id}`} className="relative overflow-hidden bg-slate-50 aspect-[4/3] block" data-testid={`product-link-${p.id}`}>
        <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        {p.featured && <span className="absolute top-4 left-4 bg-[#FF5722] text-white font-black uppercase text-[10px] tracking-widest px-3 py-1 rounded-full">Best seller</span>}
      </Link>
      <div className="p-6 flex flex-col flex-1">
        <p className="text-xs font-black uppercase tracking-widest text-[#FF5722]">{p.tagline}</p>
        <Link to={`/product/${p.id}`}><h3 className="font-display font-bold text-xl text-[#0F172A] mt-1 hover:text-[#FF5722] transition-colors">{p.name}</h3></Link>
        {p.rating > 0 && (
          <div className="flex items-center gap-1 mt-1.5" data-testid={`rating-${p.id}`}>
            {[1, 2, 3, 4, 5].map((s) => <Star key={s} size={13} className={s <= Math.round(p.rating) ? "fill-[#FF5722] text-[#FF5722]" : "text-slate-300"} />)}
            <span className="text-xs font-bold text-slate-500 ml-1">{p.rating.toFixed(1)}</span>
          </div>
        )}
        <p className="text-sm font-medium text-slate-600 mt-2 line-clamp-2">{p.description}</p>
        <div className="mt-auto pt-6 flex items-center justify-between">
          <span className="font-display font-black text-2xl text-[#0F172A]">{aud(p.price)}</span>
          <button data-testid={`add-product-${p.id}`}
            onClick={() => { add({ ref_id: p.id, name: p.name, kind: "product", price: p.price, image: p.image }); toast.success(`${p.name} added`); }}
            className="bg-[#0F172A] text-white rounded-full px-5 py-2.5 text-sm font-display font-bold uppercase tracking-wide flex items-center gap-1.5 hover:bg-[#FF5722] transition-colors">
            <Plus size={15} /> Add
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function ProductGrid({ products }) {
  const [filter, setFilter] = useState("all");
  const TABS = [{ k: "all", l: "All" }, { k: "box", l: "TV Boxes" }, { k: "accessory", l: "Accessories" }];
  const shown = filter === "all" ? products : products.filter((p) => (p.category || "box") === filter);
  return (
    <section id="shop" className="py-24 sm:py-32 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Overline>The lineup</Overline>
        <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-6xl text-[#0F172A] mt-4 leading-[0.95]">Pick your <span className="text-[#FF5722]">box.</span></h2>
        <p className="mt-5 max-w-lg text-base font-medium text-slate-600">Every box is AU stock, tested before dispatch, and backed by a 12-month warranty. Prices in AUD.</p>
        <div className="flex gap-2 mt-8" data-testid="category-filter">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setFilter(t.k)} data-testid={`filter-${t.k}`}
              className={`px-5 py-2 text-sm font-bold rounded-full border transition-colors ${filter === t.k ? "bg-[#0F172A] text-white border-[#0F172A]" : "border-slate-300 text-slate-600 hover:border-[#0F172A]"}`}>{t.l}</button>
          ))}
        </div>
        <div className="mt-12 grid sm:grid-cols-2 md:grid-cols-3 gap-6 lg:gap-8">
          {shown.map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
      </div>
    </section>
  );
}

function Plans({ plans }) {
  const { add } = useCart();
  return (
    <section id="plans" className="py-24 sm:py-32 bg-white border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Overline>Subscriptions</Overline>
        <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-6xl text-[#0F172A] mt-4 leading-[0.95]">Recharge & <span className="text-[#FF5722]">subscribe.</span></h2>
        <p className="mt-5 max-w-lg text-base font-medium text-slate-600">Prepaid IPTV subscriptions for your box. Instant activation, cancel anytime.</p>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {plans.map((pl) => (
            <motion.div key={pl.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className={`bg-white rounded-3xl p-8 flex flex-col ${pl.popular ? "border-2 border-[#FF5722] relative" : "border border-slate-200"}`} data-testid={`plan-${pl.id}`}>
              {pl.popular && <span className="absolute -top-3 left-8 bg-[#FF5722] text-white font-black uppercase text-[10px] tracking-widest px-3 py-1 rounded-full">Most popular</span>}
              <Link to={`/plan/${pl.id}`}><h3 className="font-display font-bold text-2xl text-[#0F172A] hover:text-[#FF5722] transition-colors">{pl.name}</h3></Link>
              <div className="mt-3 flex items-end gap-1">
                <span className="font-display font-black text-4xl text-[#0F172A]">{aud(pl.price)}</span>
                <span className="text-slate-500 mb-1 font-medium">/{pl.period}</span>
              </div>
              <ul className="mt-6 space-y-3 flex-1">
                {pl.features.map((f, i) => <li key={i} className="flex items-start gap-2 text-sm font-medium text-slate-600"><Check size={16} className="text-[#00C473] mt-0.5 shrink-0" /> {f}</li>)}
              </ul>
              <button data-testid={`add-plan-${pl.id}`}
                onClick={() => { add({ ref_id: pl.id, name: `${pl.name}`, kind: "plan", price: pl.price }); toast.success(`${pl.name} added`); }}
                className={`mt-8 w-full rounded-full py-3.5 font-display font-bold uppercase tracking-wide transition-colors ${pl.popular ? "bg-[#FF5722] text-white hover:bg-[#E64A19]" : "bg-[#0F172A] text-white hover:bg-[#FF5722]"}`}>Choose {pl.name}</button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

const CHAPTERS = [
  { num: "01", icon: PackageCheck, title: "AU stock, AU warranty", body: "No grey imports, no 6-week shipping roulette. Every unit sits in our Melbourne warehouse, gets bench-tested before dispatch, and carries a 12-month Australian repair warranty on the box." },
  { num: "02", icon: PlugZap, title: "Plug & play in minutes", body: "HDMI in, Wi-Fi on, details entered. That's the whole setup. Most customers go from unboxing to streaming in under five minutes — and our guides cover every step." },
  { num: "03", icon: MessagesSquare, title: "Humans on WhatsApp", body: "Stuck on a portal URL at 9pm? Message us. Real Aussie support, seven days a week, from people who actually set these boxes up every day." },
];
function Manifesto() {
  return (
    <section id="why" className="py-24 sm:py-32 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Overline>The manifesto</Overline>
        <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-6xl text-[#0F172A] mt-4 leading-[0.95] max-w-3xl">Why 12,000+ Aussies stream with us</h2>
        <div className="mt-16">
          {CHAPTERS.map((c, i) => (
            <motion.div key={c.num} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="relative border-t border-slate-200 py-12 sm:py-16 grid md:grid-cols-12 gap-8 items-start overflow-hidden" data-testid={`manifesto-chapter-${c.num}`}>
              <span className="absolute -top-8 right-0 font-display font-black text-[10rem] sm:text-[14rem] leading-none text-slate-100 select-none pointer-events-none">{c.num}</span>
              <div className="md:col-span-2 relative"><span className="font-display font-black text-sm tracking-widest text-[#FF5722]">CH. {c.num}</span></div>
              <div className="md:col-span-4 relative">
                <c.icon className="w-8 h-8 text-[#4F46E5] mb-4" />
                <h3 className="font-display font-bold text-2xl sm:text-3xl text-[#0F172A]">{c.title}</h3>
              </div>
              <p className="md:col-span-6 relative text-base sm:text-lg font-medium text-slate-600 leading-relaxed max-w-xl">{c.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  { num: "01", title: "Connect it up", body: "HDMI to your TV, power to the wall. Ethernet if you can, Wi-Fi if you can't — every box we sell does both." },
  { num: "02", title: "Join your network", body: "Pick your Wi-Fi from the list, punch in the password. The box checks for updates and handles the rest." },
  { num: "03", title: "Activate your subscription", body: "Open the pre-installed player, enter the details from your Indian TV Box subscription or recharge. We email these with your order." },
  { num: "04", title: "Start streaming", body: "Channels load, EPG populates, done. Save favourites, set up catch-up, and hand the remote to the least patient person in the house." },
];
function Guides() {
  return (
    <section id="guides" className="py-24 sm:py-32 bg-[#0F172A] text-white relative overflow-hidden">
      <div className="hero-blob w-[28rem] h-[28rem] bg-[#4F46E5]/30 -top-24 -right-24" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div className="lg:sticky lg:top-28">
            <Overline color="#FF5722">Setup guides</Overline>
            <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-6xl mt-4 leading-[0.95]">Unboxed to streaming in 5 minutes</h2>
            <p className="mt-5 max-w-md text-base font-medium text-slate-400 leading-relaxed">Four steps. No tools, no tech degree, no call to your nephew. If you do get stuck, WhatsApp us — we'll walk you through it live.</p>
            <div className="mt-10 overflow-hidden rounded-3xl border border-white/10">
              <img alt="Setting up an IPTV box" loading="lazy" className="w-full h-72 object-cover hover:scale-105 transition-transform duration-700"
                src="https://images.unsplash.com/photo-1584905066893-7d5c142ba4e1?auto=format&fit=crop&w=1000&q=80" />
            </div>
          </div>
          <div className="space-y-6">
            {STEPS.map((s) => (
              <motion.div key={s.num} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                className="group bg-white/5 border border-white/10 rounded-3xl p-8 hover:bg-white/10 hover:border-[#FF5722]/50 transition-colors duration-300" data-testid={`guide-step-${s.num}`}>
                <span className="font-display font-black text-5xl text-white/10 group-hover:text-[#FF5722]/60 transition-colors duration-300">{s.num}</span>
                <h3 className="font-display font-bold text-2xl mt-3">{s.title}</h3>
                <p className="mt-3 text-sm sm:text-base font-medium text-slate-400 leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const FAQS = [
  { q: "Do the boxes come with a subscription?", a: "Our TV boxes come bundled with a subscription (2–3 years depending on the model). You can also buy prepaid recharges separately to extend or renew." },
  { q: "How much is shipping and how fast is it?", a: "We ship Australia-wide from Melbourne. Orders placed before 11am are dispatched the same business day with tracking." },
  { q: "What's the warranty?", a: "Every box comes with a 12-month repair warranty on the set-top box itself (the remote is covered for 1 month). If a fault is ours, we repair or replace it within the warranty period. Faults caused by your internet, modem or router aren't covered." },
  { q: "Can I return it if I change my mind?", a: "We don't accept change-of-mind returns, because the box includes a 1-year repair warranty. Exceptions are considered case-by-case — raise a support ticket. Approved returns must be unused in original packaging and incur a $125 restocking fee, and recharges are prepaid and non-refundable. See our Refunds & Returns page for full details." },
  { q: "Which box should I buy?", a: "The CRUZE TV Box (rated 5/5) is our most popular all-rounder. For 8K, go MAXX GOLD or INFINITY. Not sure? WhatsApp us and we'll recommend one." },
  { q: "I'm not technical. Can you help me set it up?", a: "Absolutely. Our boxes are plug-and-play, and our team walks you through setup on WhatsApp or the phone, seven days a week." },
];
function FAQ() {
  return (
    <section id="faq" className="py-24 sm:py-32 bg-[#F8F9FA]">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <Overline>Questions</Overline>
        <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-6xl text-[#0F172A] mt-4 leading-[0.95]">Asked. <span className="text-[#FF5722]">Answered.</span></h2>
        <Accordion type="single" collapsible className="mt-12" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-200 py-2" data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="font-display font-bold text-lg sm:text-xl text-left text-[#0F172A] hover:text-[#4F46E5] hover:no-underline py-6">{f.q}</AccordionTrigger>
              <AccordionContent className="text-base font-medium text-slate-600 leading-relaxed pb-6">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-[#0F172A] text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid md:grid-cols-3 gap-10">
          <div>
            <p className="font-display font-black text-2xl uppercase flex items-center gap-2.5"><img src="/logo.png?v=2" alt="Indian TV Box logo" className="w-9 h-9 rounded-lg" />INDIANTV<span className="text-[#FF5722]">.</span>BOX</p>
            <p className="text-slate-400 text-sm font-medium mt-4 max-w-xs">Premium Indian & South Asian IPTV set-top boxes and subscriptions, proudly shipped and supported from Australia.</p>
          </div>
          <div className="text-sm text-slate-300 space-y-3 font-medium">
            <p className="uppercase tracking-[0.2em] text-xs font-black text-slate-500 mb-3">Contact</p>
            <a href="mailto:admin@indiantvbox.com.au" className="flex items-center gap-2 hover:text-white transition-colors" data-testid="footer-email"><Mail size={14} className="text-[#FF5722]" /> admin@indiantvbox.com.au</a>
            <a href="tel:+61412213515" className="flex items-center gap-2 hover:text-white transition-colors" data-testid="footer-phone"><Phone size={14} className="text-[#FF5722]" /> 0412 213 515</a>
            <p className="flex items-center gap-2"><MapPin size={14} className="text-[#FF5722]" /> Melbourne, VIC</p>
          </div>
          <div className="text-sm text-slate-300 space-y-2 font-medium">
            <p className="uppercase tracking-[0.2em] text-xs font-black text-slate-500 mb-3">Payments</p>
            <p>PayPal</p>
            <p>Direct bank transfer</p>
            <Link to="/refunds-returns" className="block mt-4 text-slate-300 hover:text-white transition-colors" data-testid="footer-refunds">Refunds &amp; Returns</Link>
            <Link to="/admin/login" className="inline-block mt-2 text-slate-500 hover:text-white transition-colors" data-testid="admin-link">Admin →</Link>
          </div>
        </div>
        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-2 text-xs text-slate-500 font-medium">
          <span>© {new Date().getFullYear()} Indian TV Box. All rights reserved.</span>
          <span>ABN 00 000 000 000</span>
        </div>
      </div>
    </footer>
  );
}

function WhatsAppFab() {
  return (
    <a href="https://wa.me/61412213515?text=Hi%20Indian%20TV%20Box%2C%20I%20need%20a%20hand%20picking%20an%20IPTV%20box"
      target="_blank" rel="noopener noreferrer" data-testid="whatsapp-fab"
      className="fixed bottom-6 right-6 z-[70] flex items-center gap-2.5 bg-[#25D366] text-white rounded-full pl-4 pr-6 py-4 shadow-[0_16px_40px_-10px_rgba(37,211,102,0.6)] font-display font-bold text-sm uppercase tracking-wide hover:scale-105 transition-transform">
      <MessageCircle size={20} className="fill-white" /> Chat with us
    </a>
  );
}

export default function Storefront() {
  const [products, setProducts] = useState([]);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    const raf = (t) => { lenis.raf(t); requestAnimationFrame(raf); };
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  const featured = products.find((p) => p.rating === 5) || products.find((p) => p.featured) || products[0];

  return (
    <div className="overflow-x-hidden bg-[#F8F9FA]">
      <Header />
      <Hero featured={featured} />
      <TickerStrip />
      <ProductGrid products={products} />
      <Plans plans={plans} />
      <Manifesto />
      <Guides />
      <FAQ />
      <Footer />
      <CartDrawer />
      <WhatsAppFab />
    </div>
  );
}
